import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  status: 'sending' | 'sent' | 'error';
}

interface AssistantChatState {
  isOpen: boolean;
  isMinimized: boolean;
  messages: ChatMessage[];
  sessionId: string | null;
  isLoading: boolean;
  error: string | null;
  unreadCount: number;
}

const initialState: AssistantChatState = {
  isOpen: false,
  isMinimized: false,
  messages: [],
  sessionId: null,
  isLoading: false,
  error: null,
  unreadCount: 0,
};

export const assistantChatSlice = createSlice({
  name: 'assistantChat',
  initialState,
  reducers: {
    toggleChat: (state) => {
      state.isOpen = !state.isOpen;
      if (state.isOpen) {
        state.unreadCount = 0;
        state.isMinimized = false;
      }
    },
    openChat: (state) => {
      state.isOpen = true;
      state.isMinimized = false;
      state.unreadCount = 0;
    },
    closeChat: (state) => {
      state.isOpen = false;
    },
    minimizeChat: (state) => {
      state.isMinimized = true;
    },
    maximizeChat: (state) => {
      state.isMinimized = false;
      state.unreadCount = 0;
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
      if (!state.isOpen || state.isMinimized) {
        if (action.payload.role === 'assistant') {
          state.unreadCount += 1;
        }
      }
    },
    updateMessageStatus: (
      state,
      action: PayloadAction<{ id: string; status: ChatMessage['status'] }>
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message) {
        message.status = action.payload.status;
      }
    },
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearChat: (state) => {
      state.messages = [];
      state.error = null;
    },
    resetChat: (state) => {
      state.messages = [];
      state.sessionId = null;
      state.error = null;
      state.unreadCount = 0;
    },
  },
});

export const {
  toggleChat,
  openChat,
  closeChat,
  minimizeChat,
  maximizeChat,
  setSessionId,
  addMessage,
  updateMessageStatus,
  setMessages,
  setLoading,
  setError,
  clearChat,
  resetChat,
} = assistantChatSlice.actions;

export default assistantChatSlice.reducer;
